import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const PLANT_STAGES = {
  1: { scale: 'scale-50', opacity: 'opacity-60' },
  2: { scale: 'scale-65', opacity: 'opacity-70' },
  3: { scale: 'scale-80', opacity: 'opacity-80' },
  4: { scale: 'scale-90', opacity: 'opacity-90' },
  5: { scale: 'scale-100', opacity: 'opacity-100' },
};

const SkillGardenPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [garden, setGarden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);

  useEffect(() => {
    fetchGarden();
  }, []);

  const fetchGarden = async () => {
    try {
      const response = await api.get('/assessment/garden');
      setGarden(response.data);
    } catch (error) {
      console.error('Failed to fetch garden:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">🌱</div>
          <p className="text-xl text-slate-600">Growing your garden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-green-50" data-testid="skill-garden-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-slate-600" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 font-fredoka ml-3">My Skill Garden</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Encouraging Message Banner */}
        <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/20 text-center">
          <div className="text-4xl mb-3">✨</div>
          <p className="text-xl font-bold text-slate-900 font-fredoka mb-2">
            {garden?.message || "Your brain is growing stronger every day!"}
          </p>
          <p className="text-slate-600">
            Watch your skills bloom as you learn!
          </p>
        </div>

        {/* Garden Visualization */}
        <div className="bg-gradient-to-b from-sky-200/50 to-green-200/50 rounded-3xl p-6 shadow-lg relative overflow-hidden">
          {/* Sky background */}
          <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-sky-300/30 to-transparent" />
          
          {/* Sun */}
          <div className="absolute top-4 right-8 w-16 h-16 bg-yellow-300 rounded-full shadow-lg animate-pulse-soft" />
          
          {/* Clouds */}
          <div className="absolute top-8 left-12 text-4xl opacity-60">☁️</div>
          <div className="absolute top-4 left-1/3 text-3xl opacity-50">☁️</div>
          
          {/* Ground */}
          <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-amber-800/30 to-transparent" />
          
          {/* Plants Grid */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 relative z-10 pt-16 pb-8">
            {garden?.garden?.map((plant, index) => {
              const stageStyle = PLANT_STAGES[plant.size] || PLANT_STAGES[3];
              
              return (
                <button
                  key={plant.id}
                  onClick={() => setSelectedPlant(plant)}
                  className="flex flex-col items-center group"
                  data-testid={`plant-${plant.id}`}
                >
                  {/* Plant */}
                  <div 
                    className={`text-5xl md:text-6xl transition-all duration-500 transform 
                      ${stageStyle.scale} ${stageStyle.opacity}
                      group-hover:scale-110 group-active:scale-95`}
                    style={{ filter: `drop-shadow(0 4px 6px ${plant.color}40)` }}
                  >
                    {plant.plant_emoji}
                  </div>
                  
                  {/* Stage Label */}
                  <div 
                    className="mt-2 px-2 py-1 rounded-full text-xs font-bold text-white shadow-md"
                    style={{ backgroundColor: plant.color }}
                  >
                    {plant.stage}
                  </div>
                  
                  {/* Plant Name */}
                  <p className="mt-1 text-xs font-medium text-slate-700 text-center leading-tight">
                    {plant.name.split(' ')[0]}
                  </p>
                </button>
              );
            })}
          </div>
          
          {/* Grass */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-around text-2xl">
            {Array(12).fill('🌿').map((grass, i) => (
              <span key={i} className="opacity-60">{grass}</span>
            ))}
          </div>
        </div>

        {/* Sessions Count */}
        <div className="text-center">
          <p className="text-slate-600">
            <span className="font-bold text-primary">{garden?.sessions_completed || 0}</span> sessions have helped your garden grow!
          </p>
        </div>

        {/* Growth Legend */}
        <div className="bg-white rounded-3xl p-6 shadow-lg">
          <h3 className="font-bold text-slate-900 font-fredoka mb-4 text-center">
            How Your Garden Grows
          </h3>
          
          <div className="flex justify-center gap-4 flex-wrap">
            {['Sprouting', 'Growing', 'Developing', 'Flourishing', 'Strong'].map((stage, index) => (
              <div key={stage} className="flex items-center gap-2">
                <span className={`text-2xl ${PLANT_STAGES[index + 1]?.scale} ${PLANT_STAGES[index + 1]?.opacity}`}>
                  🌱
                </span>
                <span className="text-sm text-slate-600">{stage}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-4">
            Every time you practice, your garden grows! 🌟
          </p>
        </div>

        {/* Tips */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-6">
          <h3 className="font-bold text-slate-900 font-fredoka mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            Garden Tips
          </h3>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <span className="text-lg">🌱</span>
              <span>Complete your daily Brain Gym Jr. to help all plants grow!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-lg">💡</span>
              <span>Using hints is smart - it helps you learn!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-lg">🔥</span>
              <span>Keep practicing every day for the best growth!</span>
            </li>
          </ul>
        </div>

        {/* Selected Plant Detail Modal */}
        {selectedPlant && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedPlant(null)}
          >
            <div 
              className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="text-7xl mb-4"
                style={{ filter: `drop-shadow(0 4px 12px ${selectedPlant.color}60)` }}
              >
                {selectedPlant.plant_emoji}
              </div>
              
              <h3 className="text-xl font-bold text-slate-900 font-fredoka mb-2">
                {selectedPlant.name}
              </h3>
              
              <div 
                className="inline-block px-4 py-2 rounded-full text-white font-bold mb-4"
                style={{ backgroundColor: selectedPlant.color }}
              >
                {selectedPlant.stage}
              </div>
              
              <p className="text-slate-600 mb-6">
                {selectedPlant.description}
              </p>
              
              <button
                onClick={() => setSelectedPlant(null)}
                className="w-full py-3 bg-primary rounded-2xl font-bold text-black"
              >
                Got it! 🌟
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SkillGardenPage;
